import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ErrorState } from '../state'
import { recordHref } from './context'
import { useRead } from './useRead'

type Kind = 'clients' | 'funds' | 'issuers'
interface SearchPage {
  items: {
    id: string
    name: string
    folder_reference?: string
    contexts?: { fund_id: string; fund_name: string }[]
  }[]
  total: number
  page: number
  has_more: boolean
}
const labels = { clients: 'Clients', funds: 'Funds', issuers: 'Issuers' }

function Group({
  kind,
  query,
  onChoose,
  pagination,
}: {
  kind: Kind
  query: string
  onChoose?: () => void
  pagination?: { page: number; onChange: (page: number) => void }
}) {
  const [localPage, setLocalPage] = useState(1)
  const page = pagination?.page ?? localPage
  const setPage = (next: number) => {
    if (pagination) pagination.onChange(next)
    else setLocalPage(next)
  }
  const [retry, setRetry] = useState(0)
  const params = new URLSearchParams({ kind, q: query, page: String(page) })
  const read = useRead<SearchPage>('/api/navigation/search?' + params, retry)
  return (
    <section className="nav-result-group" aria-label={labels[kind]}>
      <h2>
        {labels[kind]} {read.value ? <small>{read.value.total}</small> : null}
      </h2>
      {read.loading ? <p role="status">Finding {labels[kind].toLowerCase()}…</p> : null}
      {read.error ? (
        <ErrorState
          what={labels[kind].toLowerCase()}
          error={read.error}
          onRetry={() => setRetry((n) => n + 1)}
          compact
        />
      ) : null}
      {read.value?.items.map((item) => (
        <div className="nav-result" key={item.id}>
          <div>
            {kind === 'clients' ? (
              <Link to={recordHref({ kind: 'client', client_id: item.id })} onClick={onChoose}>
                <strong>{item.name}</strong>
              </Link>
            ) : kind === 'funds' ? (
              <Link to={`/v2/funds/${item.id}`} onClick={onChoose}>
                <strong>{item.name}</strong>
              </Link>
            ) : (
              <strong>{item.name}</strong>
            )}
            {item.folder_reference ? <small>Source folder: {item.folder_reference}</small> : null}
            {kind === 'issuers' ? (
              <div className="nav-context-links">
                {item.contexts?.map((context) => (
                  <Link
                    key={context.fund_id}
                    to={`/v2/funds/${context.fund_id}?focus=${encodeURIComponent('entity:' + item.id)}`}
                    onClick={onChoose}
                  >
                    {context.fund_name} ›
                  </Link>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      ))}
      {read.value?.total === 0 ? (
        <p className="nav-muted">No matching {labels[kind].toLowerCase()}.</p>
      ) : null}
      {read.value && (page > 1 || read.value.has_more) ? (
        <nav className="nav-paging" aria-label={`${labels[kind]} result pages`}>
          <button disabled={page === 1} onClick={() => setPage(page - 1)}>
            Previous
          </button>
          <span>Page {page}</span>
          <button disabled={!read.value.has_more} onClick={() => setPage(page + 1)}>
            Next
          </button>
        </nav>
      ) : null}
    </section>
  )
}
export default function SearchResults({
  query,
  onChoose,
  pagination,
}: {
  query: string
  onChoose?: () => void
  pagination?: { pages: Record<Kind, number>; onChange: (kind: Kind, page: number) => void }
}) {
  const q = query.trim()
  if (!q) return null
  return (
    <div data-testid="context-search-results">
      {(['clients', 'funds', 'issuers'] as const).map((kind) => (
        <Group
          key={kind + ':' + q}
          kind={kind}
          query={q}
          onChoose={onChoose}
          pagination={
            pagination
              ? {
                  page: pagination.pages[kind],
                  onChange: (page) => pagination.onChange(kind, page),
                }
              : undefined
          }
        />
      ))}
    </div>
  )
}
